/**
 * Security · Attackers — the sender list, extracted from Mandates
 * (founder 2026-10-20).
 *
 * Before: Security.Attackers rendered the whole Activity view with
 * `?segment=suspicious`, so the merchant saw Activity's h1 ("Every
 * visit and order, on the record"), the Activity search box, every
 * segment chip (All bots, AI agents…), identity chips, and a coral
 * ATTACK TRAFFIC IS IN SECURITY → link back to the same page. All of
 * that is Activity chrome that doesn't belong inside Security.
 *
 * After: Attackers renders only the suspicious-senders branch —
 * campaign cards on top, sender cards below — plus:
 *   - Search box "Search address or network" that filters the loaded
 *     senders by IP / as_org / country (client-side filter over what
 *     `GET /v1/sites/:id/threat-senders` returned — the server's `q`
 *     is already called for IPs, but as_org isn't indexed yet so we
 *     keep the filter local to avoid lying about totals).
 *   - A segmented switch Addresses · Networks. Networks groups the
 *     loaded senders by as_org and renders Network · Addresses ·
 *     Requests · Share. The muted line below honestly says it is
 *     based on the SENDERS LOADED (not the total): the paged senders
 *     list doesn't expose an aggregate network roll-up on the wire
 *     (no total ASN counts), so we can't invent one.
 *   - A "Got through" chip (filters to any_succeeded senders).
 *   - A NEW tag on a sender whose `first_seen` is inside the current
 *     window.
 *   - The BLOCKED · N → link still points at Security · Blocking.
 *   - The `?threat=` deep link still opens the matching sender — the
 *     parent (Security.tsx) writes it before mount and this view
 *     resolves it the same way Mandates used to.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';

import type { Site } from '@sill/types';
import type {
  BlockingResponse,
  ThreatCampaignSummary,
  ThreatSenderSummary,
} from '@sill/types/api';

import { useOptionalHttp } from '../../api/http-context.js';
import {
  getBlocking,
  listThreatCampaigns,
  listThreatSenders,
} from '../../api/threats.js';
import { Card, EmptyStateCard } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import { useIsBelowLg } from '../../lib/use-is-mobile.js';
import { CampaignCard } from '../mandates/CampaignCard.js';
import { CampaignDetail } from '../mandates/CampaignDetail.js';
import { SenderCard } from '../mandates/SenderCard.js';
import { SenderDetail } from '../mandates/SenderDetail.js';
import type { SecuritySection } from './Security.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

const THREAT_ALERT_ID_RE = /^tht_[A-Za-z0-9]{26}$/;

function readThreatParam(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = new URL(window.location.href).searchParams.get('threat');
    return v !== null && THREAT_ALERT_ID_RE.test(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Founder bug 2026-10-20 (Report 6) — the "open from blocklist" link
 * seeds `?q=<address>` so the row stays findable when the blocklist
 * row lacks a `source_threat_alert_id`. The value is UNVALIDATED
 * attacker-controlled; the client-side filter already copes with any
 * free text (one `.toLowerCase().includes(...)` per sender), so no
 * further sanitisation is needed here.
 */
function readSearchParam(): string {
  if (typeof window === 'undefined') return '';
  try {
    const v = new URL(window.location.href).searchParams.get('q');
    return typeof v === 'string' ? v.slice(0, 100) : '';
  } catch {
    return '';
  }
}

/**
 * Founder bug 2026-10-20 (coordinator item 11) — campaign email links
 * (`?campaign=<id>`) must open Attackers scrolled to the matching
 * campaign card. The id is a ULID-ish string from `threat_campaign`;
 * we validate shape here so an attacker query can't surface a bogus
 * value in the UI.
 */
const CAMPAIGN_ID_RE = /^[A-Za-z0-9_]{10,40}$/;
function readCampaignParam(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = new URL(window.location.href).searchParams.get('campaign');
    return v !== null && CAMPAIGN_ID_RE.test(v) ? v : null;
  } catch {
    return null;
  }
}

/** Poll cadence — matches Mandates' grouped-list cadence. */
const SENDERS_POLL_INTERVAL_MS = 30_000;

export type AttackersMode = 'addresses' | 'networks';

export interface AttackersViewProps {
  readonly site: Site;
  readonly onOpenBlocking: (section: SecuritySection) => void;
}

/**
 * Group loaded senders by as_org for the Networks mode. Unknown orgs
 * roll up under "Unknown network". The share is sender-count / total.
 */
interface NetworkRow {
  readonly org: string;
  readonly senders: readonly ThreatSenderSummary[];
  readonly addresses: number;
  readonly requests: number;
  readonly share: number;
}

export function groupSendersByNetwork(
  senders: readonly ThreatSenderSummary[],
): readonly NetworkRow[] {
  const map = new Map<string, { senders: ThreatSenderSummary[]; requests: number }>();
  for (const s of senders) {
    const org = s.as_org !== null && s.as_org.length > 0 ? s.as_org : 'Unknown network';
    const entry = map.get(org) ?? { senders: [], requests: 0 };
    entry.senders.push(s);
    entry.requests += s.request_count;
    map.set(org, entry);
  }
  const total = senders.length;
  const rows: NetworkRow[] = [];
  for (const [org, v] of map.entries()) {
    rows.push({
      org,
      senders: v.senders,
      addresses: v.senders.length,
      requests: v.requests,
      share: total === 0 ? 0 : v.senders.length / total,
    });
  }
  rows.sort((a, b) => b.addresses - a.addresses || b.requests - a.requests);
  return rows;
}

/** First-seen "newness" test. Founder review 2026-10-20: a 7-day window
 *  tagged nearly every sender (attack addresses are mostly fresh), so NEW
 *  means first seen on this site in the last 24 hours. */
export function isNewSender(
  sender: ThreatSenderSummary,
  windowDays = 1,
): boolean {
  if (typeof sender.first_seen !== 'string' || sender.first_seen.length === 0) return false;
  const t = Date.parse(sender.first_seen);
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= windowDays * 24 * 60 * 60 * 1000;
}

/**
 * Does the loaded sender match the search text? Used as instant client
 * feedback while the debounced server-side `q` fetch is in flight —
 * the server-side search is authoritative (and canonical for the total
 * match count); this filter just keeps the list looking responsive on
 * the already-loaded page.
 */
function senderMatchesSearch(s: ThreatSenderSummary, needle: string): boolean {
  if (needle.length === 0) return true;
  const n = needle.toLowerCase();
  if (s.sender_ip_display.toLowerCase().includes(n)) return true;
  if (s.as_org !== null && s.as_org.toLowerCase().includes(n)) return true;
  if (s.country !== null && s.country.toLowerCase().includes(n)) return true;
  if (s.region !== null && s.region.toLowerCase().includes(n)) return true;
  for (const label of s.class_labels) {
    if (label.toLowerCase().includes(n)) return true;
  }
  return false;
}

/** Debounce window for the Attackers search box server call. */
const ATTACKERS_SEARCH_DEBOUNCE_MS = 250;

export function AttackersView({ site, onOpenBlocking }: AttackersViewProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const isMobile = useIsBelowLg();

  const [senders, setSenders] = useState<readonly ThreatSenderSummary[] | null>(null);
  const [campaigns, setCampaigns] = useState<readonly ThreatCampaignSummary[] | null>(null);
  const [campaignBySender, setCampaignBySender] = useState<Readonly<Record<string, string>>>({});
  const [selectedSender, setSelectedSender] = useState<ThreatSenderSummary | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<ThreatCampaignSummary | null>(null);
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [blocking, setBlocking] = useState<BlockingResponse['blocking'] | null>(null);
  const [liveBlocks, setLiveBlocks] = useState(0);
  const [query, setQuery] = useState(() => readSearchParam());
  // Debounced server-side `q` — the typed value is applied to the
  // threat-senders fetch 250 ms after the user stops typing. Instant
  // feedback is still provided client-side by `senderMatchesSearch`.
  const [debouncedQuery, setDebouncedQuery] = useState(() => readSearchParam());
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query), ATTACKERS_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);
  const [mode, setMode] = useState<AttackersMode>('addresses');
  const [gotThrough, setGotThrough] = useState(false);
  // Founder bug 2026-10-20 (Report 1) — "I blocked it. It disappeared
  // so I couldn't find it in the feed." The sender wasn't gone from
  // the DB — the ORDER BY demotes handled rows to the end of the list,
  // which can push a blocked sender off the first page. The Blocked
  // chip filters the loaded list so blocked senders are one click away;
  // paired with the "Open" link from Security › Blocking they stay
  // findable without re-querying.
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const pendingSenderRefRef = useRef<string | null>(readThreatParam());
  const pendingCampaignIdRef = useRef<string | null>(readCampaignParam());
  const didAutoSelectRef = useRef(false);
  const detailPaneRef = useRef<HTMLDivElement | null>(null);

  /* ------------------------------------------------------------------ *
   *  Fetches — senders + campaigns + blocking.                         *
   * ------------------------------------------------------------------ */

  // Server-side total count — authoritative figure for "N senders match
  // your search", independent of what the current page returned.
  const [totalMatches, setTotalMatches] = useState<number | null>(null);

  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    const site_id = site.site_id;
    const q_trim = debouncedQuery.trim();

    const fetchAll = async (): Promise<void> => {
      try {
        const [sendersRes, campaignsRes] = await Promise.all([
          listThreatSenders(http, site_id, q_trim.length > 0 ? { q: q_trim } : {}),
          listThreatCampaigns(http, site_id, {}),
        ]);
        if (cancelled) return;
        setSenders(sendersRes.senders);
        setCampaignBySender(sendersRes.campaign_by_sender ?? {});
        setCampaigns(campaignsRes.campaigns);
        setTotalMatches(typeof sendersRes.total_count === 'number' ? sendersRes.total_count : null);
        setLoadError(false);
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchAll();
    const handle = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void fetchAll();
    }, SENDERS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [http, site.site_id, reload, debouncedQuery]);

  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    void getBlocking(http, site.site_id)
      .then((r) => {
        if (cancelled || r === undefined || r === null) return;
        setBlocking(r.blocking);
        setLiveBlocks(r.counts?.live_blocks ?? 0);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [http, site.site_id, reload]);

  /* ------------------------------------------------------------------ *
   *  Auto-select + deep-link resolution.                               *
   * ------------------------------------------------------------------ */

  // Campaign deep-link: open the campaign detail when `?campaign=<id>`
  // was present on mount AND the campaigns list has loaded. Consumes
  // the URL param on resolve so refresh doesn't re-open.
  useEffect(() => {
    if (campaigns === null) return;
    const pending_campaign = pendingCampaignIdRef.current;
    if (pending_campaign === null) return;
    const match = campaigns.find((c) => c.campaign_id === pending_campaign);
    pendingCampaignIdRef.current = null;
    if (match !== undefined) {
      setSelectedSender(null);
      setSelectedCampaign(match);
      setDrawerOpen(true);
    }
    if (typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('campaign')) {
          url.searchParams.delete('campaign');
          window.history.replaceState(window.history.state, '', url.toString());
        }
      } catch { /* URL hygiene is enhancement only */ }
    }
  }, [campaigns]);

  useEffect(() => {
    if (senders === null) return;
    const pending = pendingSenderRefRef.current;
    if (pending !== null) {
      const match = senders.find((s) => s.sender_ref === pending);
      pendingSenderRefRef.current = null;
      if (match !== undefined) {
        setSelectedSender(match);
        setDrawerOpen(true);
      } else {
        // The deep link's id doesn't head any loaded group. The detail
        // endpoint resolves either way, but we must NOT fabricate a
        // severity colour for the stub (an older build pinned
        // `max_severity: 'medium'`, which painted the panel in that
        // colour even for a critical sender that was off the page).
        // Mark it as a loading placeholder — SenderDetail refetches by
        // sender_ref and renders the real header.
        setSelectedSender({
          sender_ref: pending,
          sender_ip_display: '',
          ip_status: 'hidden',
          country: null, region: null, as_org: null,
          network_type: 'unknown',
          // Neutral severity + flag so the detail renders a muted
          // "Loading sender…" header, never a coloured banner.
          max_severity: 'low',
          any_succeeded: false,
          class_labels: [], class_summary: '',
          alert_count: 0, requests_display: '',
          request_count: 0,
          first_seen: new Date().toISOString(),
          last_seen: new Date().toISOString(),
          state: 'none', block_expires_at: null,
        });
        setDrawerOpen(true);
      }
      // Consume the URL one-shot.
      if (typeof window !== 'undefined') {
        try {
          const url = new URL(window.location.href);
          if (url.searchParams.has('threat')) {
            url.searchParams.delete('threat');
            window.history.replaceState(window.history.state, '', url.toString());
          }
        } catch { /* URL hygiene is enhancement only */ }
      }
      return;
    }
    // First-load auto-select (desktop only).
    if (
      !didAutoSelectRef.current
      && !isMobile
      && senders.length > 0
      && selectedSender === null
      && selectedCampaign === null
    ) {
      didAutoSelectRef.current = true;
      setSelectedSender(senders[0]!);
    }
  }, [senders, isMobile, selectedSender, selectedCampaign]);

  // Reset the sticky detail pane on selection change.
  useEffect(() => {
    const el = detailPaneRef.current;
    if (el !== null) el.scrollTop = 0;
  }, [selectedSender, selectedCampaign]);

  /* ------------------------------------------------------------------ *
   *  Filtered list + grouping.                                         *
   * ------------------------------------------------------------------ */

  const filteredSenders = useMemo((): readonly ThreatSenderSummary[] => {
    const list = senders ?? [];
    const needle = query.trim();
    return list.filter((s) => {
      if (gotThrough && !s.any_succeeded) return false;
      if (blockedOnly && s.state !== 'blocked' && s.state !== 'block_saved') return false;
      return senderMatchesSearch(s, needle);
    });
  }, [senders, query, gotThrough, blockedOnly]);

  const filteredCampaigns = useMemo((): readonly ThreatCampaignSummary[] => {
    const list = campaigns ?? [];
    const needle = query.trim().toLowerCase();
    return list.filter((c) => {
      if (gotThrough && !c.any_succeeded) return false;
      // Campaign cards don't carry a per-campaign block state yet; the
      // Blocked chip only narrows the sender list, not campaigns.
      if (blockedOnly) return false;
      if (needle.length === 0) return true;
      const org = c.as_org?.toLowerCase() ?? '';
      return org.includes(needle);
    });
  }, [campaigns, query, gotThrough, blockedOnly]);

  const networks = useMemo(() => groupSendersByNetwork(filteredSenders), [filteredSenders]);

  const openSender = useCallback((s: ThreatSenderSummary): void => {
    setSelectedCampaign(null);
    setSelectedSender(s);
    setDrawerOpen(true);
  }, []);

  const openCampaign = useCallback((c: ThreatCampaignSummary): void => {
    setSelectedSender(null);
    setSelectedCampaign(c);
    setDrawerOpen(true);
  }, []);

  /* ------------------------------------------------------------------ *
   *  Swipe prev/next in the mobile drawer — walk campaigns, then       *
   *  senders, in display order (same as Mandates did before).          *
   * ------------------------------------------------------------------ */
  const navItems = useMemo((): ReadonlyArray<
    { readonly kind: 'campaign'; readonly c: ThreatCampaignSummary }
    | { readonly kind: 'sender'; readonly s: ThreatSenderSummary }
  > => [
    ...(mode === 'addresses' ? filteredCampaigns.map((c) => ({ kind: 'campaign' as const, c })) : []),
    ...filteredSenders.map((s) => ({ kind: 'sender' as const, s })),
  ], [filteredCampaigns, filteredSenders, mode]);
  const selectedIndex = useMemo(() => {
    if (selectedCampaign !== null) {
      return navItems.findIndex((it) => it.kind === 'campaign' && it.c.campaign_id === selectedCampaign.campaign_id);
    }
    if (selectedSender !== null) {
      return navItems.findIndex((it) => it.kind === 'sender' && it.s.sender_ref === selectedSender.sender_ref);
    }
    return -1;
  }, [navItems, selectedCampaign, selectedSender]);
  const canGoPrev = selectedIndex > 0;
  const canGoNext = selectedIndex >= 0 && selectedIndex < navItems.length - 1;
  const goToIndex = (i: number): void => {
    const it = navItems[i];
    if (it === undefined) return;
    if (it.kind === 'campaign') openCampaign(it.c);
    else openSender(it.s);
  };
  const goToPrev = (): void => { if (canGoPrev) goToIndex(selectedIndex - 1); };
  const goToNext = (): void => { if (canGoNext) goToIndex(selectedIndex + 1); };
  const swipeTouchStart = useRef<{ x: number; y: number } | null>(null);
  const onSwipeTouchStart = (e: React.TouchEvent): void => {
    swipeTouchStart.current = { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY };
  };
  const onSwipeTouchEnd = (e: React.TouchEvent): void => {
    if (swipeTouchStart.current === null) return;
    const dx = e.changedTouches[0]!.clientX - swipeTouchStart.current.x;
    const dy = e.changedTouches[0]!.clientY - swipeTouchStart.current.y;
    swipeTouchStart.current = null;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) goToNext();
    else goToPrev();
  };

  /* ------------------------------------------------------------------ *
   *  Render.                                                           *
   * ------------------------------------------------------------------ */

  const showBlockedLink = blocking !== null && (liveBlocks > 0 || blocking.mode === 'on');

  const senderList = senders ?? [];
  return (
    <div data-testid="attackers-view">
      {/* Row — search, Addresses · Networks, Got through, BLOCKED → */}
      <div
        className="flex items-center gap-3 mb-3 flex-wrap"
        data-testid="attackers-filter-row"
      >
        <div
          className="flex items-center gap-2 px-3 py-2 flex-1 min-w-[200px]"
          style={{
            background: C.surface,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 6,
          }}
        >
          <Search size={14} color={C.muted} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search country, city, IP, network or path"
            data-testid="attackers-search"
            aria-label="Search country, city, IP, network or path"
            className="flex-1"
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
              padding: 0,
            }}
          />
          {debouncedQuery.trim().length > 0 && totalMatches !== null ? (
            <span
              data-testid="attackers-search-total"
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                letterSpacing: 1,
                color: C.muted,
                whiteSpace: 'nowrap',
              }}
            >
              {totalMatches.toLocaleString('en-US')} {totalMatches === 1 ? 'MATCH' : 'MATCHES'}
            </span>
          ) : null}
          {query.length > 0 ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              style={{
                width: 18,
                height: 18,
                color: C.muted,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={12} />
            </button>
          ) : null}
        </div>
        {/* Addresses · Networks — Overview segmented control. */}
        <div
          className="flex items-center"
          role="group"
          aria-label="Attackers mode"
          data-testid="attackers-mode"
          style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 2 }}
        >
          {(['addresses', 'networks'] as const).map((m, i, arr) => {
            const sel = mode === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                aria-pressed={sel}
                data-testid={`attackers-mode-${m}`}
                className="px-2.5 py-1.5"
                style={{
                  fontFamily: monoFont,
                  fontSize: 11,
                  letterSpacing: 1,
                  fontWeight: 500,
                  color: sel ? C.text : C.muted,
                  background: sel ? C.surface2 : 'transparent',
                  borderRight: i < arr.length - 1 ? `1px solid ${C.borderSoft}` : 'none',
                  cursor: 'pointer',
                }}
              >
                {m === 'addresses' ? 'ADDRESSES' : 'NETWORKS'}
              </button>
            );
          })}
        </div>
        {/* Got through — surfaces senders that got any 2xx. */}
        <button
          type="button"
          data-testid="attackers-filter-got-through"
          aria-pressed={gotThrough}
          onClick={() => setGotThrough((v) => !v)}
          className="px-3 py-1.5"
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 1,
            color: gotThrough ? C.coralFg : C.muted,
            background: gotThrough ? C.coralDim : 'transparent',
            border: `1px solid ${gotThrough ? C.coralFg : 'transparent'}`,
            borderRadius: 2,
            cursor: 'pointer',
          }}
        >
          GOT THROUGH
        </button>
        {/* Founder bug 2026-10-20 (Report 1) — a BLOCKED chip so a
            merchant who just blocked a sender can still find it. */}
        <button
          type="button"
          data-testid="attackers-filter-blocked"
          aria-pressed={blockedOnly}
          onClick={() => setBlockedOnly((v) => !v)}
          className="px-3 py-1.5"
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 1,
            color: blockedOnly ? C.text : C.muted,
            background: blockedOnly ? C.surface2 : 'transparent',
            border: `1px solid ${blockedOnly ? C.border : 'transparent'}`,
            borderRadius: 2,
            cursor: 'pointer',
          }}
        >
          BLOCKED
        </button>
        {showBlockedLink ? (
          <button
            type="button"
            data-testid="attackers-blocked-link"
            onClick={() => onOpenBlocking('blocking')}
            style={{
              marginLeft: 'auto',
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: C.limeFg,
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 2,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            BLOCKED &middot; {liveBlocks.toLocaleString('en-US')} &rarr;
          </button>
        ) : null}
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* List column. */}
        <div className="flex-1 min-w-0">
          <Card>
            {loading && senderList.length === 0 ? (
              <div style={{ padding: '18px 16px', fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: C.muted }}>
                LOADING…
              </div>
            ) : loadError && senderList.length === 0 ? (
              <EmptyStateCard
                label="Couldn't load the sender list"
                hint="Sill will try again in a moment."
              />
            ) : senderList.length === 0 ? (
              <EmptyStateCard
                label="No suspicious senders"
                hint="No attack senders have landed on this site. New senders appear as soon as they probe your Worker."
              />
            ) : mode === 'networks' ? (
              <NetworksList
                rows={networks}
                onSelectNetwork={(net) => {
                  // A network row lists that network's addresses: switch to
                  // Addresses filtered by the network name. Senders with no
                  // network name can't be searched for, so open the first.
                  const named = net.senders.some((x) => typeof x.as_org === 'string' && x.as_org === net.org);
                  if (named) {
                    setQuery(net.org);
                    setMode('addresses');
                  } else {
                    const first = net.senders[0];
                    if (first !== undefined) openSender(first);
                  }
                }}
                totalSendersLoaded={filteredSenders.length}
              />
            ) : (
              <AddressesList
                campaigns={filteredCampaigns}
                senders={filteredSenders}
                campaignBySender={campaignBySender}
                selectedSenderRef={selectedSender?.sender_ref ?? null}
                selectedCampaignId={selectedCampaign?.campaign_id ?? null}
                onSelectSender={openSender}
                onSelectCampaign={openCampaign}
                query={query}
                anyLoadedSenders={senderList.length > 0}
                anyLoadedCampaigns={(campaigns ?? []).length > 0}
                gotThrough={gotThrough}
              />
            )}
          </Card>
        </div>

        {/* Desktop detail pane. */}
        <div
          ref={detailPaneRef}
          data-selection-key={selectedCampaign !== null
            ? `c:${selectedCampaign.campaign_id}`
            : selectedSender !== null ? `s:${selectedSender.sender_ref}` : 'empty'}
          className="hidden lg:block lg:w-[640px] lg:flex-shrink-0 lg:self-start lg:sticky lg:top-24 lg:max-h-[calc(100vh_-_7rem)] lg:overflow-y-auto"
        >
          {!isMobile ? (
            selectedCampaign !== null ? (
              <CampaignDetail
                siteId={site.site_id}
                siteDomain={site.domain ?? ''}
                campaign={selectedCampaign}
                onChanged={() => setReload((n) => n + 1)}
                onSelectMember={(sender_ref) => {
                  const match = (senders ?? []).find((s) => s.sender_ref === sender_ref);
                  if (match !== undefined) openSender(match);
                }}
              />
            ) : selectedSender !== null ? (
              <SenderDetail
                siteId={site.site_id}
                siteDomain={site.domain ?? null}
                sender={selectedSender}
                onChanged={() => setReload((n) => n + 1)}
              />
            ) : null
          ) : null}
        </div>
      </div>

      {/* Mobile detail — full-screen panel replacing the list. */}
      <AnimatePresence>
        {isMobile && drawerOpen && (selectedSender !== null || selectedCampaign !== null) ? (
          <motion.div
            key="attackers-mobile-detail"
            data-testid="attackers-mobile-detail"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.22, ease: [0.32, 0, 0.67, 0] }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 50,
              background: C.bg,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
            onTouchStart={onSwipeTouchStart}
            onTouchEnd={onSwipeTouchEnd}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 16px',
                borderBottom: `1px solid ${C.border}`,
                position: 'sticky',
                top: 0,
                background: C.bg,
                zIndex: 1,
              }}
            >
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Back to list"
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '4px 0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontFamily: monoFont,
                  fontSize: 10,
                  letterSpacing: 1.5,
                  color: C.muted,
                }}
              >
                <ChevronLeft size={14} /> BACK
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <button
                  type="button"
                  onClick={goToPrev}
                  disabled={!canGoPrev}
                  aria-label="Previous sender"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '6px 8px',
                    cursor: canGoPrev ? 'pointer' : 'default',
                    color: canGoPrev ? C.muted : C.dim,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
                <span
                  style={{
                    fontFamily: monoFont,
                    fontSize: 9,
                    color: C.dim,
                    letterSpacing: 1,
                    minWidth: 40,
                    textAlign: 'center',
                  }}
                >
                  {selectedIndex >= 0 ? `${selectedIndex + 1} / ${navItems.length}` : ''}
                </span>
                <button
                  type="button"
                  onClick={goToNext}
                  disabled={!canGoNext}
                  aria-label="Next sender"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '6px 8px',
                    cursor: canGoNext ? 'pointer' : 'default',
                    color: canGoNext ? C.muted : C.dim,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
            <div className="p-4">
              {selectedCampaign !== null ? (
                <CampaignDetail
                  siteId={site.site_id}
                  siteDomain={site.domain ?? ''}
                  campaign={selectedCampaign}
                  onChanged={() => setReload((n) => n + 1)}
                  onSelectMember={(sender_ref) => {
                    const match = (senders ?? []).find((s) => s.sender_ref === sender_ref);
                    if (match !== undefined) openSender(match);
                  }}
                />
              ) : selectedSender !== null ? (
                <SenderDetail
                  siteId={site.site_id}
                  siteDomain={site.domain ?? null}
                  sender={selectedSender}
                  onChanged={() => setReload((n) => n + 1)}
                />
              ) : null}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------- *
 *  Addresses mode — campaign cards above the sender cards.            *
 * ------------------------------------------------------------------- */

interface AddressesListProps {
  readonly campaigns: readonly ThreatCampaignSummary[];
  readonly senders: readonly ThreatSenderSummary[];
  readonly campaignBySender: Readonly<Record<string, string>>;
  readonly selectedSenderRef: string | null;
  readonly selectedCampaignId: string | null;
  readonly onSelectSender: (s: ThreatSenderSummary) => void;
  readonly onSelectCampaign: (c: ThreatCampaignSummary) => void;
  readonly query: string;
  readonly anyLoadedSenders: boolean;
  readonly anyLoadedCampaigns: boolean;
  readonly gotThrough: boolean;
}

function AddressesList({
  campaigns,
  senders,
  campaignBySender,
  selectedSenderRef,
  selectedCampaignId,
  onSelectSender,
  onSelectCampaign,
  query,
  anyLoadedSenders,
  anyLoadedCampaigns,
  gotThrough,
}: AddressesListProps): JSX.Element {
  const C = useC();
  const needle = query.trim();
  const noMatches = senders.length === 0 && campaigns.length === 0 && (anyLoadedSenders || anyLoadedCampaigns);

  return (
    <div>
      {noMatches && needle.length > 0 ? (
        <div
          data-testid="attackers-empty-search"
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            padding: '14px 16px',
          }}
        >
          No senders match &ldquo;{needle}&rdquo;.
        </div>
      ) : null}
      {noMatches && needle.length === 0 && gotThrough ? (
        <div
          data-testid="attackers-empty-got-through"
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            padding: '14px 16px',
          }}
        >
          No senders got through in this window.
        </div>
      ) : null}
      {campaigns.length > 0 ? (
        <div data-testid="attackers-campaigns" style={{ padding: '14px 16px 4px 16px' }}>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              letterSpacing: 2,
              color: C.muted,
              marginBottom: 8,
            }}
          >
            CAMPAIGNS &middot; {campaigns.length}
          </div>
          {campaigns.map((c) => (
            <CampaignCard
              key={c.campaign_id}
              campaign={c}
              isSelected={selectedCampaignId === c.campaign_id}
              onSelect={() => onSelectCampaign(c)}
            />
          ))}
        </div>
      ) : null}
      {senders.length > 0 ? (
        <>
          <div
            data-testid="attackers-attacks-kicker"
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              letterSpacing: 2,
              color: C.muted,
              padding: '10px 16px 6px 16px',
            }}
          >
            ATTACKS &middot; {senders.length} SENDER{senders.length === 1 ? '' : 'S'}
          </div>
          {senders.map((s, i) => {
            const isNew = isNewSender(s);
            return (
              <div key={s.sender_ref} style={{ position: 'relative' }}>
                <SenderCard
                  sender={s}
                  isNew={isNew}
                  isSelected={selectedSenderRef === s.sender_ref}
                  isLast={i === senders.length - 1}
                  onSelect={() => onSelectSender(s)}
                  campaignId={campaignBySender[s.sender_ref] ?? null}
                  onSelectCampaign={(campaign_id) => {
                    const match = campaigns.find((c) => c.campaign_id === campaign_id);
                    if (match !== undefined) onSelectCampaign(match);
                  }}
                />
              </div>
            );
          })}
        </>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------- *
 *  Networks mode — senders grouped by as_org.                         *
 * ------------------------------------------------------------------- */

interface NetworksListProps {
  readonly rows: readonly NetworkRow[];
  readonly onSelectNetwork: (row: NetworkRow) => void;
  readonly totalSendersLoaded: number;
}

function NetworksList({
  rows,
  onSelectNetwork,
  totalSendersLoaded,
}: NetworksListProps): JSX.Element {
  const C = useC();
  if (rows.length === 0) {
    return (
      <div
        data-testid="attackers-networks-empty"
        style={{
          padding: '14px 16px',
          fontFamily: sansFont,
          fontSize: 13,
          color: C.muted,
        }}
      >
        No networks match the current filters.
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }} data-testid="attackers-networks-table">
      <table
        style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 13 }}
      >
        <thead>
          <tr style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' }}>
            <th style={{ textAlign: 'left', padding: '12px 14px', fontWeight: 500 }}>Network</th>
            <th style={{ textAlign: 'right', padding: '12px 14px', fontWeight: 500 }}>Addresses</th>
            <th style={{ textAlign: 'right', padding: '12px 14px', fontWeight: 500 }}>Requests</th>
            <th style={{ textAlign: 'right', padding: '12px 14px', fontWeight: 500 }}>Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.org}
              data-testid={`attackers-network-row-${row.org}`}
              style={{ borderTop: `1px solid ${C.borderSoft}`, cursor: 'pointer' }}
              onClick={() => onSelectNetwork(row)}
            >
              <td style={{ padding: '12px 14px', color: C.text }}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onSelectNetwork(row); }}
                  aria-label={`Show addresses on ${row.org}`}
                  style={{ background: 'transparent', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', textAlign: 'left' }}
                >
                  {row.org}
                </button>
              </td>
              <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: monoFont, color: C.text, fontVariantNumeric: 'tabular-nums' }}>
                {row.addresses.toLocaleString('en-US')}
              </td>
              <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: monoFont, color: C.text, fontVariantNumeric: 'tabular-nums' }}>
                {row.requests.toLocaleString('en-US')}
              </td>
              <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: monoFont, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
                {Math.round(row.share * 100)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div
        data-testid="attackers-networks-footnote"
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          padding: '10px 16px',
        }}
      >
        Based on the {totalSendersLoaded.toLocaleString('en-US')} most recent {totalSendersLoaded === 1 ? 'sender' : 'senders'}.
      </div>
    </div>
  );
}
